import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Rewrite multi-file uploads into indexed line item properties so Shopify
 * receives one property key per file instead of repeated values on one key.
 * @param {HTMLFormElement} form
 * @param {FormData} formData
 * @returns {FormData}
 */
function normalizeMultiFileUploadProperties(form, formData) {
  const multiFileInputs = Array.from(form.elements).filter((input) =>
    input instanceof HTMLInputElement &&
    input.type === 'file' && input.multiple &&
    !input.matches(':disabled') &&
    input.closest('.product-file-upload, .product-personalization')
  );

  multiFileInputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement) || !input.files?.length || !input.name) return;

    const match = input.name.match(/^properties\[(.*)\]$/);
    const propertyKey = match?.[1]?.trim();

    if (!propertyKey) return;

    formData.delete(input.name);

    Array.from(input.files).forEach((file, index) => {
      formData.append(`properties[${propertyKey} ${index + 1}]`, file);
    });
  });

  return formData;
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{item_count: number, items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {boolean} */
  #submissionPreparationInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.dataset.baseMinimumQuantity = this.dataset.baseMinimumQuantity || this.dataset.minimumQuantity || '1';
    this.addEventListener('change', this.#onMinimumQuantityPropertyChange, { signal });
    this.addEventListener('input', this.#onMinimumQuantityPropertyChange, { signal });
    this.#syncMinimumQuantityFromProperties();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartQty = cart.items.reduce(
      (total, item) => total + (item.variant_id.toString() === variantIdInput.value.toString() ? item.quantity : 0),
      0
    );

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<Cart|null>} The confirmed cart, if it could be fetched
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  async handleSubmit(event) {
    event.preventDefault();

    const grid = this.querySelector('variant-quantity-grid');
    if (grid?.active) {
      if (this.#submissionPreparationInProgress || grid.busy || grid.loading) return;
      this.#submissionPreparationInProgress = true;
      this.ariaBusy = 'true';
      grid.setProgress('preparing');
      try {
        await yieldToMainThread();
        let ready;
        do {
          const deadline = Date.now() + 10000;
          while (this.#variantChangeInProgress) {
            if (Date.now() > deadline) throw new Error(grid.text('failed'));
            await new Promise((resolve) => setTimeout(resolve, 25));
          }
          if (grid.variantError) throw grid.variantError;
          ready = await this.#prepareCustomizersForSubmit();
        } while (ready && this.#variantChangeInProgress);
        if (!ready) return;
        this.#syncMinimumQuantityFromProperties();
        const form = this.querySelector('form');
        if (!form?.reportValidity()) return;
        const snapshot = normalizeMultiFileUploadProperties(form, new FormData(form));
        const minimum = Number(this.dataset.quantityGridMinimum || 1);
        if (minimum > 1) snapshot.set('properties[_minimum_quantity]', String(minimum));
        await grid.submit(snapshot, minimum);
      } catch (error) {
        grid.submissionError = error.message || grid.text('failed');
        grid.message('[data-grid-error]', grid.submissionError);
      } finally {
        this.#submissionPreparationInProgress = false;
        this.removeAttribute('aria-busy');
        grid.setProgress(null);
      }
      return;
    }

    if (this.#submissionPreparationInProgress) return;

    this.#submissionPreparationInProgress = true;
    this.ariaBusy = 'true';

    let customizationReady = false;

    try {
      customizationReady = await this.#prepareCustomizersForSubmit();
    } finally {
      this.#submissionPreparationInProgress = false;
      this.ariaBusy = null;
    }

    if (!customizationReady) return;

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {Promise<boolean>} */
  async #prepareCustomizersForSubmit() {
    const form = this.querySelector('form');
    if (!form) return false;

    for (const element of form.querySelectorAll('patch-designer, engraving-preview, apparel-preview')) {
      const customizer = /** @type {HTMLElement & { prepareForSubmit?: () => Promise<boolean> }} */ (element);
      if (!customizer.prepareForSubmit) continue;
      if (!(await customizer.prepareForSubmit())) return false;
    }

    return true;
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @param {Event} event */
  #onMinimumQuantityPropertyChange = (event) => {
    const target = event.target;

    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLSelectElement) &&
      !(target instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    if (!target.name?.startsWith('properties[')) return;

    this.#syncMinimumQuantityFromProperties();
  };

  /** @returns {number} */
  #getBaseMinimumQuantity() {
    if (this.querySelector('variant-quantity-grid')?.active) return Number(this.dataset.quantityGridBaseMinimum) || 1;
    return Number.parseInt(this.dataset.baseMinimumQuantity || this.dataset.minimumQuantity || '1', 10) || 1;
  }

  /**
   * @param {HTMLElement} block
   * @returns {{enabled?: boolean, optionMinimums?: Record<string, number|string>} | null}
   */
  #parseMinimumQuantityConfig(block) {
    try {
      return JSON.parse(block.dataset.minimumQuantityConfig || '{}');
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} propertyKey
   * @returns {string[]}
   */
  #getPropertyValues(form, propertyKey) {
    const formData = new FormData(form);
    return formData
      .getAll(`properties[${propertyKey}]`)
      .flatMap((value) =>
        value
          .toString()
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      );
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLElement} block
   * @param {string} propertyKey
   * @returns {boolean}
   */
  #blockHasMinimumQuantityValue(form, block, propertyKey) {
    const blockType = block.dataset.minimumQuantityType || block.dataset.feeType || '';

    if (blockType === 'file') {
      const fileInput = block.querySelector('input[type="file"]');
      return fileInput instanceof HTMLInputElement && !!fileInput.files?.length;
    }

    if (blockType === 'personalization') {
      const textKey = block.dataset.personalizationTextKey;
      const hasText =
        !!textKey && this.#getPropertyValues(form, textKey).some((value) => value.trim() !== '');
      const fileInput = block.querySelector('input[type="file"]');
      const hasFile = fileInput instanceof HTMLInputElement && !!fileInput.files?.length;

      return hasText || hasFile;
    }

    if (blockType === 'checkbox') {
      const checkbox = Array.from(block.querySelectorAll('input[type="checkbox"]')).find(
        (input) => input instanceof HTMLInputElement && input.name === `properties[${propertyKey}]`
      );
      return checkbox instanceof HTMLInputElement && checkbox.checked;
    }

    return this.#getPropertyValues(form, propertyKey).some((value) => value.trim() !== '');
  }

  /** @returns {number} */
  #getEffectiveMinimumQuantity() {
    const form = this.querySelector('form');
    let effectiveMinimum = this.#getBaseMinimumQuantity();

    if (!form) return effectiveMinimum;

    const blocks = form.querySelectorAll('[data-minimum-quantity-config]');
    blocks.forEach((block) => {
      if (!(block instanceof HTMLElement)) return;

      const config = this.#parseMinimumQuantityConfig(block);
      const propertyKey = block.dataset.key || block.dataset.minimumQuantityPropertyKey;

      if (!config?.enabled || !propertyKey) return;

      const valueMinimum = Number.parseInt(config.minimum?.toString() || '', 10);
      if (
        !Number.isNaN(valueMinimum) &&
        valueMinimum > effectiveMinimum &&
        this.#blockHasMinimumQuantityValue(form, block, propertyKey)
      ) {
        effectiveMinimum = valueMinimum;
      }

      if (!config.optionMinimums) return;

      const selectedValues = this.#getPropertyValues(form, propertyKey);
      selectedValues.forEach((value) => {
        const lookupKey = value.toLowerCase().trim();
        const optionMinimum = Number.parseInt(config.optionMinimums?.[lookupKey]?.toString() || '', 10);

        if (!Number.isNaN(optionMinimum) && optionMinimum > effectiveMinimum) {
          effectiveMinimum = optionMinimum;
        }
      });
    });

    return effectiveMinimum;
  }

  #syncMinimumQuantityFromProperties() {
    const effectiveMinimum = this.#getEffectiveMinimumQuantity();
    const minimumString = effectiveMinimum.toString();
    const quantitySelector = this.refs.quantitySelector;
    const previousMinimum = Number.parseInt(this.dataset.minimumQuantity || this.dataset.baseMinimumQuantity || '1', 10) || 1;

    this.dataset.minimumQuantity = minimumString;
    if (this.querySelector('variant-quantity-grid')?.active) {
      this.dataset.quantityGridMinimum = minimumString;
      return;
    }

    if (!quantitySelector) return;

    quantitySelector.dataset.minimumQuantity = minimumString;

    const quantityInput = quantitySelector.refs?.quantityInput || quantitySelector.querySelector?.('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.min = minimumString;
      quantityInput.dataset.min = minimumString;
    }

    const currentQuantity = Number.parseInt(quantitySelector.getValue?.() || quantityInput?.value || '0', 10) || 0;
    if (currentQuantity < effectiveMinimum) {
      quantitySelector.setValue?.(minimumString);
    } else if (effectiveMinimum < previousMinimum && currentQuantity === previousMinimum) {
      quantitySelector.setValue?.(minimumString);
    }

    quantitySelector.updateButtonStates?.();
  }

  /** @param {FormData} formData */
  #setMinimumQuantityLineItemProperty(formData) {
    const effectiveMinimum = this.#getEffectiveMinimumQuantity();
    const baseMinimum = this.#getBaseMinimumQuantity();

    formData.delete('properties[_minimum_quantity]');

    if (effectiveMinimum > baseMinimum) {
      formData.set('properties[_minimum_quantity]', effectiveMinimum.toString());
    }
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    this.#syncMinimumQuantityFromProperties();

    // Check minimum quantity requirement
    if (this.refs.quantitySelector) {
      const minimumQuantity = parseInt(this.dataset.minimumQuantity || '0');
      const currentQuantity = parseInt(this.refs.quantitySelector.getValue?.() || '0');

      if (minimumQuantity > 0 && currentQuantity < minimumQuantity) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        this.refs.quantitySelector.setValue?.(minimumQuantity.toString());
        this.refs.quantitySelector.updateButtonStates?.();

        const errorTemplate = this.dataset.quantityErrorMin || '';
        let errorMessage = errorTemplate.replace('{{ minimum }}', minimumQuantity.toString());

        if (!errorMessage || errorMessage === errorTemplate) {
          errorMessage = `Minimum quantity required: ${minimumQuantity}`;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          addToCartTextError.setAttribute('aria-live', 'assertive');

          const iconWrapper = addToCartTextError.querySelector('.svg-wrapper');

          const textNodes = [];
          for (let i = 0; i < addToCartTextError.childNodes.length; i++) {
            const node = addToCartTextError.childNodes[i];
            if (node.nodeType === Node.TEXT_NODE) {
              textNodes.push(node);
            }
          }
          textNodes.forEach((node) => node.remove());

          const errorTextNode = document.createTextNode(` ${errorMessage}`);
          if (iconWrapper && iconWrapper.nextSibling) {
            addToCartTextError.insertBefore(errorTextNode, iconWrapper.nextSibling);
          } else {
            addToCartTextError.appendChild(errorTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = normalizeMultiFileUploadProperties(form, new FormData(form));
    this.#setMinimumQuantityLineItemProperty(formData);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }
    const variantId = String(formData.get('id') || '');
    const previousVariantQuantity = this.refs.quantitySelector?.getCurrentValues?.().cartQuantity ?? 0;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const notice = response.description || response.message;
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = notice;
            } else {
              addToCartTextError.appendChild(document.createTextNode(notice));
            }
            this.#setLiveRegionText(notice);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          // An inventory error can still add the available units. Reconcile before announcing a cart add.
          const cart = await this.#fetchAndUpdateCartQuantity();
          const actualVariantQuantity = cart?.items.reduce(
            (total, item) => total + (item.variant_id.toString() === variantId ? item.quantity : 0),
            0
          ) ?? previousVariantQuantity;
          const addedQuantity = Math.max(0, actualVariantQuantity - previousVariantQuantity);
          if (cart && addedQuantity > 0) {
            this.dispatchEvent(
              new CartAddEvent(cart, this.id, {
                didError: true,
                source: 'product-form-component',
                itemCount: addedQuantity,
                cartCount: cart.item_count,
                productId: this.dataset.productId,
                notice,
              })
            );
          }

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();
          const actualVariantQuantity = cart?.items.reduce(
            (total, item) => total + (item.variant_id.toString() === variantId ? item.quantity : 0),
            0
          );
          const addedQuantity = actualVariantQuantity === undefined
            ? Number(formData.get('quantity')) || Number(this.dataset.quantityDefault)
            : Math.max(0, actualVariantQuantity - previousVariantQuantity);

          this.dispatchEvent(
            new CartAddEvent(cart || {}, id.toString(), {
              source: 'product-form-component',
              itemCount: addedQuantity,
              cartCount: cart?.item_count,
              productId: this.dataset.productId,
              variantId: id.toString(),
              cartLineKey: response.key,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;
    const previousCartCount = document.querySelector('cart-icon')?.currentCartCount ?? null;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const notice = response.description || response.message;
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = notice;
            } else {
              addToCartTextError.appendChild(document.createTextNode(notice));
            }
            this.#setLiveRegionText(notice);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          const cart = await this.#fetchAndUpdateCartQuantity();
          const addedQuantity = cart && previousCartCount !== null
            ? Math.max(0, cart.item_count - previousCartCount)
            : 0;
          if (cart && addedQuantity > 0) {
            this.dispatchEvent(
              new CartAddEvent(cart, this.id, {
                didError: true,
                source: 'product-form-component',
                itemCount: addedQuantity,
                cartCount: cart.item_count,
                productId: this.dataset.productId,
                notice,
              })
            );
          }
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = cart && previousCartCount !== null
          ? Math.max(0, cart.item_count - previousCartCount)
          : items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart || {}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            cartCount: cart?.item_count,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    const newProductForm = event.detail.data.html.querySelector('product-form-component');
    if (newProductForm instanceof HTMLElement && newProductForm.dataset.minimumQuantity) {
      this.dataset.baseMinimumQuantity = newProductForm.dataset.minimumQuantity;
    }

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    if (this.querySelector('variant-quantity-grid')?.active) {
      this.#syncMinimumQuantityFromProperties();
      this.querySelector('variant-quantity-grid').mount();
      return;
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );
    const newQuantitySelector = newQuantityInput?.closest('quantity-selector-component');

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.setCartQuantity?.(Number(newQuantityInput.dataset.cartQuantity || 0));
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step,
        newQuantitySelector?.dataset.stockLimit || null,
        newQuantitySelector?.dataset.ruleMax || null,
        newQuantitySelector?.dataset.variantId || null
      );
    }

    this.#syncMinimumQuantityFromProperties();

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          this.refs.quantitySelector.setCartQuantity?.(Number(newQuantityInputElement.dataset.cartQuantity || 0));
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
            newQuantitySelector?.dataset.stockLimit || null,
            newQuantitySelector?.dataset.ruleMax || null,
            newQuantitySelector?.dataset.variantId || null
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    this.querySelector('price-per-item')?.refreshPriceBreaks?.();

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    this.#syncMinimumQuantityFromProperties();

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures && !this.refs.quantitySelector?.dataset.stockLimit) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
